import { ExpressionSpecification, type Map as MaplibreMap } from "maplibre-gl";
import type { FeatureCollection, Geometry } from "geojson";

import { COLOR_KEYS, COLORS } from "@/constants/colors";
import { DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";

import { generateColorRangesUniqueStops } from "../common";
import { generateOrdinalColorExpression } from "../map";
import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";

/**
 * Adds a clustered GeoJSON source to the map for point data
 */
export const addClusterSource = (
  map: MaplibreMap | null,
  sourceId: string,
  pointsData: FeatureCollection<Geometry>
): void => {
  if (!map) return;

  const parsedPointsData: FeatureCollection<Geometry> = {
    ...pointsData,
    features: pointsData.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        unique_id: feature.properties?.unique_id || index
      }
    }))
  };

  map.addSource(sourceId, {
    type: "geojson",
    data: parsedPointsData,
    cluster: true,
    clusterMaxZoom: 15,
    clusterRadius: 40,
    promoteId: "unique_id"
  });
};

/**
 * Generates the common layer configuration for cluster layers
 */
const addClusterLayers = ({
  isNewUIDatasetConfigEnabled,
  isRankingLabelEnabled,
  map,
  dataConfig,
  category,
  style,
  colorExpression,
  beforeId
}: {
  isNewUIDatasetConfigEnabled: boolean;
  isRankingLabelEnabled: boolean;
  map: MaplibreMap | null;
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string };
  style: DatasetStyle;
  category: string;
  colorExpression: ExpressionSpecification;
  beforeId?: string;
}): void => {
  if (!map || !dataConfig.sublayers) return;

  // Add the cluster circles layer
  map.addLayer(
    {
      id: dataConfig.sublayers.circle,
      type: "circle",
      source: dataConfig.id,
      filter: ["has", "point_count"],
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "circle-color": COLORS[style.colorFamily][COLOR_KEYS[5]] || "#11b4da",
        "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
        "circle-opacity": style.fillOpacity ?? 0.7,
        "circle-stroke-width": 1,
        "circle-stroke-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#FFCF01", // Visible outline when selected
          "#ffffff00" // No outline when not selected
        ]
      }
    },
    beforeId
  );

  // Add the count text layer
  map.addLayer(
    {
      id: dataConfig.sublayers.count,
      type: "symbol",
      source: dataConfig.id,
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-size": 12,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-offset": [0, 0.1],
        "text-anchor": "center",
        visibility: style?.visibility || "visible"
      },
      paint: {
        "text-color": "#ffffff"
      }
    },
    beforeId
  );

  // Add the unclustered points layer
  map.addLayer(
    {
      id: dataConfig.sublayers.unclustered,
      type: "circle",
      source: dataConfig.id,
      filter: ["!", ["has", "point_count"]],
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "circle-color": [
          "case",
          ["has", category],
          colorExpression,
          "#74c476"
        ] as ExpressionSpecification,
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          8,
          6
        ] as ExpressionSpecification,
        "circle-opacity": style.fillOpacity ?? 0.7,
        "circle-stroke-width": 1,
        "circle-stroke-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#FFCF01", // Visible outline when selected
          "#ffffff" // No outline when not selected
        ]
      }
    },
    beforeId
  );

  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataConfig, style, beforeId);
  addRankLabelLayer(isRankingLabelEnabled, map, dataConfig, style, beforeId);
};

/**
 * Adds cluster visualization layers to the map with ordinal color scale
 */
export const addClusterOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  // Create ordinal color expression using match
  const ordinalColorExpression = generateOrdinalColorExpression(
    style.colorRanges,
    style.colorFamily,
    category,
    style.colorSteps || 10
  );

  // Use the common function with the ordinal color expression
  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    colorExpression: ordinalColorExpression,
    beforeId
  });
};

/**
 * Adds cluster visualization layers to the map with quantile color scale
 */
export const addClusterQuantileLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string,
  beforeId?: string
): void => {
  if (!map || !dataConfig.sublayers) return;

  const colorRanges = generateColorRangesUniqueStops(
    style.colorRanges,
    style.colorFamily,
    style.colorSteps || 10
  );

  const isSingleColorRange = style.colorRanges.length === 1;

  // Create quantile color expression using step
  const quantileColorExpression: ExpressionSpecification = [
    "step",
    ["get", category],
    colorRanges[0][1],
    ...(isSingleColorRange
      ? colorRanges[0]
      : colorRanges.slice(1).flatMap(([value, color]) => [value, color]))
  ];

  // Use the common function with the quantile color expression
  addClusterLayers({
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    dataConfig,
    style,
    category,
    colorExpression: quantileColorExpression,
    beforeId
  });
};
